import { GAME } from '../config';
import type { FactionId } from '../../shared/types';
import { AFFINITY_HALO_COLORS } from './enemy';
import type { BossPatternState, BossMoveId, BossPatternPhase } from '../ai/boss-pattern';
import { createBossPatternState } from '../ai/boss-pattern';

export type BossAttack =
  | { kind: 'single-shot'; angleRad: number; speedPxPerSec: number; damage: number }
  | { kind: 'spawn-minions'; count: number; bonusSpeed?: number }
  | { kind: 'silence-player'; durationMs: number }
  | { kind: 'fan-shot'; angles: number[]; speedPxPerSec: number; damage: number }
  /**
   * boss-bullet-hell (老闆 2026-05-31「子彈很多一次發個 20 顆」) — 360° 環形彈幕。
   * 從 boss 中心平均射出 `count` 顆一般彈,鋪滿全圓(不朝玩家收斂),製造彈幕壓迫感。
   * 場景以 _fireBossRing 生成(走既有 _spawnBossBullet 路徑)。純遊戲機制
   * (學習與遊戲分離:不涉及答題、不寫回修煉值)。
   *
   * combat-polish (老闆 2026-06「ring 太簡單一眼看穿」):新增 3 個「擇優」變化欄位,
   * 讓環有挑戰但**仍可躲**(K-3 鐵則:看得出、躲得掉)。全部 optional,舊呼叫(只給
   * count/speed/damage)= 原本單層均勻環,回歸不變:
   *   - `layers`     : 同心多層環 (2 = 雙層)。外層角度自動錯開半步,玩家要「找對縫」。
   *   - `gapRad`     : 留一個明顯缺口 (角寬 rad);此弧內不放彈,玩家衝缺口即安全。
   *   - `rotateDps`  : 整環緩慢旋轉 (度/秒,切向分量);彈幕轉動但速度不變仍可閃。
   * 速度維持 150~180 (K-3 閃得過)。
   */
  | {
      kind: 'ring-burst';
      count: number;
      speedPxPerSec: number;
      damage: number;
      /** 同心環層數 (≥1;預設 1 = 單層)。外層角度錯開半步,逼玩家找對縫。 */
      layers?: number;
      /** 環上安全缺口的角寬 (rad);此弧內不放彈 (預設 0 = 無缺口,完整環)。 */
      gapRad?: number;
      /** 整環旋轉角速度 (度/秒;預設 0)。每顆初速帶切向分量 → 彈幕緩慢旋轉。 */
      rotateDps?: number;
    }
  | { kind: 'freeze-player'; durationMs: number }
  | { kind: 'beam-cast'; angleRad: number; speedPxPerSec: number; damage: number }
  | { kind: 'silence-wave'; radiusPx: number; durationMs: number }
  /**
   * boss-movesets (老闆 2026-05-31「每一魔王招式要不一樣」+「多加雷射光」):
   * 雷射光 — 一條從 boss 朝玩家方向的「持續貫穿光束」。分兩階段:先細線「預警」
   * (warnMs),讓 K-3 看得到方向先閃開;再變粗「開火」(fireMs),期間玩家碰到
   * 光束受傷。角度在預警觸發當下鎖定 (不會持續追),所以一定躲得掉 (老闆鐵則:
   * 看得出、躲得掉)。場景以 _executeBossAttack 的 'laser-beam' case 渲染 +
   * _laserBeams 狀態做命中判定。純遊戲機制 (學習與遊戲分離:不寫回修煉值)。
   */
  | { kind: 'laser-beam'; warnMs: number; fireMs: number; thickness: number; damage: number }
  /**
   * boss-movesets (老闆「多加魔法元素」):
   * 魔法彈 — 有獨特視覺 (紫/藍魔法感) 且「波浪 (sine) 前進」的彈,跟普通直線彈
   * 明顯不同。amplitude/period 控制波幅與週期;速度適中讓 K-3 看波形預判閃避。
   * 場景以 'magic-bolt' case 生成、_updateBossBullets 對 magic 彈每幀加垂直擺動。
   * 純遊戲機制 (學習與遊戲分離)。
   */
  | {
      kind: 'magic-bolt';
      /** 同時射出的魔法彈數 (朝玩家略散開) */
      count: number;
      /** 主行進速度 px/秒 (適中:180~260) */
      speedPxPerSec: number;
      /** 波幅 px (垂直於行進方向的最大偏移) */
      amplitudePx: number;
      /** 波動週期 ms (一個完整 sine 來回) */
      periodMs: number;
      damage: number;
    }
  /**
   * boss-movesets (老闆「多加緩速元素」):
   * 緩速 — 命中或範圍效果,讓玩家移速暫時 ×slowMult (非定身,仍能移動避免挫折)。
   * 場景以 'slow-player' case 設 playerSlowedUntilMs,_playerSpeedNow 乘 slowMult。
   * 有畫面藍光提示。純遊戲機制 (學習與遊戲分離)。
   */
  | { kind: 'slow-player'; durationMs: number; slowMult: number }
  /**
   * 追蹤彈 (homing) — boss-behavior 升級。每幀朝玩家方向轉一點,受
   * turnRatePerSec 限制(度/秒),所以「可閃」不是必中。純遊戲機制
   * (學習與遊戲分離:不涉及答題)。場景以 _spawnHomingBullet 生成、
   * stepHomingBullet 每幀導引,並受 _enemyFreezeUntilMs 凍結。
   */
  | {
      kind: 'homing-shot';
      /** 同時射出的追蹤彈數 */
      count: number;
      /** 子彈速度 px/秒(建議比一般彈略慢,給玩家閃避空間) */
      speedPxPerSec: number;
      /** 每秒最大轉向角度(度/秒)。90~150 = 可閃;>240 趨近必中 */
      turnRatePerSec: number;
      damage: number;
    }
  /**
   * boss-aoe (老闆 2026-06「魔王應具備…大面積傷害等能力」+「要有地方可以躲」):
   * 大面積衝擊波 — 從 boss 位置鎖定一個圓心,先畫一個「會擴張的預警圈」(warnMs),
   * 圈在預警期間就完整顯示「危險範圍」,玩家看得到、走出圈外即安全;預警結束才
   * 「轟」一下做判定:**只在圈內**的玩家受傷 (站圈外 0 傷)。warnMs 必須夠長
   * (≥ SHOCKWAVE_MIN_SAFE_WARN_MS) 讓 K-3 從容走出去 → 老闆鐵則「看得出、躲得掉」。
   * 場景以 _spawnShockwave 生成、_updateShockwaves 推進判定、_drawShockwaves 渲染
   * (複用 ring/explode 視覺風格)。純遊戲機制 (學習與遊戲分離:不寫回修煉值)。
   */
  | {
      kind: 'shockwave';
      /** 衝擊波最大半徑 (px) — 預警圈/判定圈的範圍。 */
      radiusPx: number;
      /** 預警時間 (ms) — 圈擴張到滿、玩家走出去的時間。必須夠長 (可躲)。 */
      warnMs: number;
      /** 命中傷害 (僅圈內)。 */
      damage: number;
    };

export type BossAttackHomingShot = Extract<BossAttack, { kind: 'homing-shot' }>;

export type BossAttackBeamCast = { kind: 'beam-cast'; angleRad: number; speedPxPerSec: number; damage: number };
export type BossAttackMinionBuff = { kind: 'spawn-minions'; count: number; bonusSpeed?: number };

export interface BossDef {
  id: string;
  displayName: string;
  hp: number;
  speedPxPerSec: number;
  size: number;
  color: number;
  /** SVG sprite asset key (registered in ASSETS and preloaded in BattleScene). */
  assetKey: string;
  /** @deprecated Geometric fallback shape -- superseded by SVG sprites. */
  shape: 'diamond' | 'double-circle' | 'hexagon';
  faction: 'celestial' | 'runic' | 'elemental' | 'techno' | 'dominion';
  /**
   * The faction this boss is weak to — matching player skill deals x1.5 damage.
   * Use applyAffinityMultiplier(skillFaction, boss.def.affinityFaction, baseDamage).
   *
   * TODO (boss damage application): needs scene wire-up in
   * horizontal-battle-scene.ts:_applySkillToBoss when that file is available.
   */
  affinityFaction: FactionId;
  /** Difficulty tier (defaults to 1 if omitted). */
  tier?: 1 | 2 | 3;
  /** Boss intro dialog shown as a subtitle at entrance. */
  introDialog?: string;
  /** Full-screen camera background tint applied during this boss fight (hex color, e.g. 0x1a0033). */
  arenaTint?: number;
  /** Entrance cinematic style. Defaults to 'descend'. */
  entranceCinematic?: 'descend' | 'teleport-in' | 'rise-from-ground' | 'phase-in';
  /** Death cinematic style. Defaults to 'explode-standard'. */
  deathCinematic?: 'explode-standard' | 'fragment-into-pieces' | 'crystal-shatter' | 'fade-into-data';
  /**
   * Unique per-boss gimmick mechanic.
   * 'teleport'               -- teleports to random x when HP < 50%, every 5s
   * 'mirror-player'          -- mirrors player's last spread-shot angle (delayed)
   * 'spawn-orbiting-minions' -- 3 minions orbit the boss continuously
   * 'phase-shield'           -- damage-triggered or phase-based invincibility
   * 'echo-attacks'           -- single-shots echo once after 1s delay
   */
  uniqueGimmick?: 'teleport' | 'mirror-player' | 'spawn-orbiting-minions' | 'phase-shield' | 'echo-attacks';
  attackACooldownMs: number;
  attackBCooldownMs: number;
  phase2HpThreshold: number;
  /**
   * Multi-phase thresholds (HP ratio, descending). When present, overrides
   * phase2HpThreshold for phase computation.
   * [0.66, 0.33] -- 3 phases at 66% and 33% HP.
   * [0.75, 0.5, 0.25] -- 4 phases at 75%, 50%, 25% HP.
   */
  phaseThresholds?: number[];
  /** Pre-attack-B telegraph window (ms). */
  attackBWarningMs: number;
  tickAttacks(self: Boss, nowMs: number, playerX: number): BossAttack[];
}

/** Y-offset (px) of the warning ring centre above the boss sprite centre. */
export const BOSS_WARNING_RING_Y_OFFSET = -60;

/** Warning ring render descriptor — pure data, no Phaser dependency. */
export interface WarningRingState {
  /** 0 = not showing; >0 and <1 = charging; 1 = complete / fire. */
  progress: number;
  /** Whether the ring is currently active (visible). */
  active: boolean;
  /** World x of ring centre. */
  x: number;
  /** World y of ring centre. */
  y: number;
  /** Tint color taken from boss def.color. */
  color: number;
}

/**
 * learnable-boss-proto: pure-data descriptor for the move-specific telegraph +
 * recover (breach) visual. Renderer-agnostic — the scene reads it each frame
 * and draws a move-distinct telegraph (laser = red aim line preview; sniper =
 * muzzle charge-up toward the player) during TELEGRAPH, and a "boss exposed /
 * dimmed" cue during RECOVER. Lets the kid learn "this telegraph → dodge like
 * this" and "this dimmed boss → focus fire now".
 */
export interface BossTelegraphState {
  /** Current pattern phase. */
  phase: BossPatternPhase;
  /** The move id currently being telegraphed / fired (e.g. 'sniper', 'laser'). */
  moveId: BossMoveId;
  /** Telegraph fill progress 0→1 (charge-up animation). */
  telegraphProgress: number;
  /** Recover (breach window) progress 0→1 (boss dim→restore animation). */
  recoverProgress: number;
  /** True while the boss is in its vulnerable breach window (RECOVER). */
  vulnerable: boolean;
}

/**
 * Berserk-mode cooldown multiplier (×0.66 → ~1.5× attack frequency).
 * Activates when boss HP drops below `BERSERK_HP_RATIO` of max HP.
 */
export const BERSERK_COOLDOWN_MULTIPLIER = 0.66;

/** HP ratio threshold (inclusive) at which berserk mode engages. */
export const BERSERK_HP_RATIO = 0.5;

/** Alpha applied to the red tint overlay drawn while in berserk mode. */
export const BERSERK_TINT_ALPHA = 0.3;

/** Red tint colour applied as an overlay in berserk mode. */
export const BERSERK_TINT_COLOR = 0xff0000;

/** Pure-data descriptor for the berserk-mode visual cue (renderer-agnostic). */
export interface BerserkOverlayDescriptor {
  active: boolean;
  x: number;
  y: number;
  radius: number;
  color: number;
  alpha: number;
}

export class Boss {
  x: number;
  y: number;
  hp: number;
  alive = true;
  private targetY: number;
  lastAttackAMs = -Infinity;
  lastAttackBMs = -Infinity;
  def: BossDef;

  /** Max HP captured at spawn (immutable for the lifetime of the boss). */
  readonly hpMax: number;

  /**
   * Phase-2 berserk mode flag.
   * Set true when hp drops to <= hpMax * BERSERK_HP_RATIO; never resets
   * (one-way transition — bosses don't heal back out of berserk).
   */
  private _berserkMode = false;

  /** Warning ring state — updated by tickWarningRing(), read by renderer. */
  warningRing: WarningRingState = { progress: 0, active: false, x: 0, y: 0, color: 0 };
  /** Internal: timestamp when attack-B warning phase started (-Infinity = idle). */
  private _warningStartMs = -Infinity;

  /**
   * learnable-boss-proto (老闆 2026-06「魔王要可學習、可精通、有攻防節奏」):
   * 攻防循環狀態機的可變狀態 (IDLE→TELEGRAPH→FIRE→RECOVER)。目前只有 SHU_MO
   * 的 tickAttacks 會驅動它 (原型階段);FU_HUN/YUAN_SU 仍走舊 cooldown,不碰此欄位。
   * 由 advanceBossPattern(this.patternState, dt, cfg, berserk) 每幀推進。
   */
  patternState: BossPatternState = createBossPatternState();
  /** Internal: nowMs of the previous pattern tick, to derive dtMs (-1 = first tick). */
  _lastPatternTickMs = -1;
  /**
   * 最近一次 pattern tick 的快照 (給 scene 畫「招式專屬預警 + 破綻」視覺)。
   * 由 tickAttacks 內部更新;scene 透過 getTelegraphDescriptor() 讀。
   */
  patternTelegraph: BossTelegraphState = {
    phase: 'IDLE',
    moveId: '',
    telegraphProgress: 0,
    recoverProgress: 0,
    vulnerable: false,
  };

  constructor(def: BossDef, position: { x: number; y: number }) {
    this.def = def;
    this.x = position.x;
    this.y = position.y;
    this.hp = def.hp;
    this.hpMax = def.hp;
    this.targetY = GAME.height * 0.2;
    this.warningRing.color = def.color;
  }

  get phase(): 1 | 2 | 3 | 4 {
    if (this.def.phaseThresholds) {
      const hpRatio = this.hp / this.def.hp;
      let p = 1;
      for (const t of this.def.phaseThresholds) {
        if (hpRatio < t) p++;
      }
      return p as 1 | 2 | 3 | 4;
    }
    return this.hp <= this.def.phase2HpThreshold ? 2 : 1;
  }

  /** Whether the boss is currently in berserk mode (hp <= hpMax * 50%). */
  get berserkMode(): boolean {
    return this._berserkMode;
  }

  /**
   * Effective attack-A cooldown — accounts for berserk multiplier.
   * Closures inside BossDef.tickAttacks should use this getter (not
   * `self.def.attackACooldownMs`) so berserk speed-up applies.
   */
  get effectiveAttackACooldownMs(): number {
    return this._berserkMode
      ? this.def.attackACooldownMs * BERSERK_COOLDOWN_MULTIPLIER
      : this.def.attackACooldownMs;
  }

  /** Effective attack-B cooldown — accounts for berserk multiplier. */
  get effectiveAttackBCooldownMs(): number {
    return this._berserkMode
      ? this.def.attackBCooldownMs * BERSERK_COOLDOWN_MULTIPLIER
      : this.def.attackBCooldownMs;
  }

  update(dtSec: number): void {
    if (!this.alive) return;
    if (this.y < this.targetY) this.y += this.def.speedPxPerSec * dtSec;
  }

  takeDamage(amount: number): void {
    this.hp = Math.max(0, this.hp - amount);
    // Berserk engages once and stays on for the rest of the fight.
    if (!this._berserkMode && this.hp <= this.hpMax * BERSERK_HP_RATIO && this.hp > 0) {
      this._berserkMode = true;
    }
    if (this.hp === 0) this.alive = false;
  }

  /**
   * Pure-data descriptor for the berserk visual cue.  Renderer reads this
   * and draws a red overlay (Phaser Graphics.fillStyle + fillCircle) when
   * `active` is true.  Y matches the boss centre.
   */
  getBerserkOverlayDescriptor(): BerserkOverlayDescriptor {
    return {
      active: this._berserkMode && this.alive,
      x: this.x,
      y: this.y,
      radius: this.def.size / 2,
      color: BERSERK_TINT_COLOR,
      alpha: BERSERK_TINT_ALPHA,
    };
  }

  /**
   * Draws the berserk red tint overlay using a Phaser Graphics object.
   * Call from the scene's render loop AFTER drawing the boss sprite so
   * the tint sits on top.  Pass the same Graphics handle each frame and
   * `.clear()` it before re-draw.
   */
  drawBerserkOverlay(graphics: {
    fillStyle(color: number, alpha: number): void;
    fillCircle(x: number, y: number, r: number): void;
  }): void {
    if (!this._berserkMode || !this.alive) return;
    const d = this.getBerserkOverlayDescriptor();
    graphics.fillStyle(d.color, d.alpha);
    graphics.fillCircle(d.x, d.y, d.radius);
  }

  /**
   * Begin the attack-B warning phase.
   * Call this right when attack-B is scheduled but before the delay elapses.
   */
  startWarningRing(nowMs: number): void {
    this._warningStartMs = nowMs;
    this.warningRing.active = true;
    this.warningRing.progress = 0;
    this.warningRing.color = this.def.color;
    this._syncRingPosition();
  }

  /**
   * Advance the ring each frame.  Returns progress [0, 1].
   * Deactivates automatically when warning duration elapses.
   */
  tickWarningRing(nowMs: number): number {
    if (!this.warningRing.active) return 0;
    const elapsed = nowMs - this._warningStartMs;
    const progress = Math.min(1, elapsed / this.def.attackBWarningMs);
    this.warningRing.progress = progress;
    this._syncRingPosition();
    if (progress >= 1) {
      this.warningRing.active = false;
    }
    return progress;
  }

  /** Hide and reset the ring (e.g. after attack fires or boss dies). */
  clearWarningRing(): void {
    this.warningRing.active = false;
    this.warningRing.progress = 0;
    this._warningStartMs = -Infinity;
  }

  /** Returns the faction this boss is weak to (for use with applyAffinityMultiplier). */
  getAffinityFaction(): FactionId {
    return this.def.affinityFaction;
  }

  /**
   * Draws a pulsing faction-colored halo around the boss using a Phaser Graphics object.
   * Halo radius = def.size / 2 + 12 (slightly larger than the boss sprite).
   * Alpha pulses between 0.3 and 0.5 over a 1.5s cycle — pass `nowMs` each frame.
   *
   * Call from the scene's render loop before drawing the boss sprite;
   * clear the Graphics object each frame before re-drawing.
   */
  drawAffinityHalo(
    graphics: { fillStyle(color: number, alpha: number): void; fillCircle(x: number, y: number, r: number): void },
    nowMs: number,
  ): void {
    const color = AFFINITY_HALO_COLORS[this.def.affinityFaction];
    // Pulse alpha 0.3 -> 0.5 over 1.5s period
    const pulse = 0.4 + 0.1 * Math.sin((nowMs / 1500) * Math.PI * 2);
    const radius = this.def.size / 2 + 12;
    graphics.fillStyle(color, pulse);
    graphics.fillCircle(this.x, this.y, radius);
  }

  /** Returns a snapshot of the ring state for the renderer. */
  getWarningRingDescriptor(): WarningRingState {
    return { ...this.warningRing };
  }

  /**
   * learnable-boss-proto: snapshot of the pattern telegraph / breach state for
   * the renderer. SHU_MO's tickAttacks writes `patternTelegraph` each frame;
   * the scene reads this to draw move-distinct telegraphs and the breach cue.
   */
  getTelegraphDescriptor(): BossTelegraphState {
    return { ...this.patternTelegraph };
  }

  private _syncRingPosition(): void {
    this.warningRing.x = this.x;
    this.warningRing.y = this.y + BOSS_WARNING_RING_Y_OFFSET;
  }
}

// ─── Homing bullet steering (boss-behavior 升級) ──────────────────────────────
//
// 純函式 — 不依賴 Phaser,可單測。Scene 端 bossBullet 加 `homing` 旗、
// `speedPxPerSec`、`turnRatePerSec` 三個欄位,_updateBossBullets 對 homing
// 彈每幀呼叫 stepHomingBullet 重新導引速度方向,再用 vx/vy 位移。
// 學習與遊戲分離:純遊戲機制。

/**
 * 同時存在的 homing 彈硬上限(效能 + 畫面清晰 + 仍可閃)。
 * 超過時 scene 回收最舊的 homing 彈。
 */
export const MAX_HOMING_BULLETS = 12;

/** 把角度正規化到 (-π, π]。 */
export function normalizeAngle(a: number): number {
  while (a > Math.PI) a -= 2 * Math.PI;
  while (a <= -Math.PI) a += 2 * Math.PI;
  return a;
}

export interface HomingBulletState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** px/秒;若省略則由 hypot(vx,vy) 推得 */
  speedPxPerSec?: number;
  /** 度/秒,本幀最大轉向角 */
  turnRatePerSec?: number;
}

/**
 * 對單一 homing 子彈執行「一幀」的轉向 + 位移。
 *
 * - 速度方向每幀朝 (targetX, targetY) 轉,但單幀最多轉 turnRatePerSec*dt 度,
 *   所以玩家可以靠橫移甩開(turnRate 適中時非必中)。
 * - mutate 傳入物件的 vx/vy/x/y,並回傳新的 {vx, vy}(方便測試)。
 *
 * @param deltaMs 本幀毫秒
 */
export function stepHomingBullet(
  bullet: HomingBulletState,
  targetX: number,
  targetY: number,
  deltaMs: number,
): { vx: number; vy: number } {
  const dt = deltaMs / 1000;
  const speed = bullet.speedPxPerSec ?? (Math.hypot(bullet.vx, bullet.vy) || 1);
  const turnRate = bullet.turnRatePerSec ?? 120; // 度/秒 fallback

  const currentAngle = Math.atan2(bullet.vy, bullet.vx);
  const desiredAngle = Math.atan2(targetY - bullet.y, targetX - bullet.x);
  const diff = normalizeAngle(desiredAngle - currentAngle);

  const maxStep = ((turnRate * Math.PI) / 180) * dt; // 本幀允許的最大轉向(弧度)
  const step = Math.max(-maxStep, Math.min(maxStep, diff));
  const newAngle = currentAngle + step;

  bullet.vx = Math.cos(newAngle) * speed;
  bullet.vy = Math.sin(newAngle) * speed;
  bullet.x += bullet.vx * dt;
  bullet.y += bullet.vy * dt;
  return { vx: bullet.vx, vy: bullet.vy };
}

// ─── boss-aoe: 衝擊波 (shockwave) 純函式 ──────────────────────────────────────
//
// 大面積 AoE,有「夠長的預警圈」可躲 (老闆鐵則:看得出、躲得掉)。圓心在 spawn
// 當下鎖定 (不追玩家),所以玩家只要在預警期間走出圈外就 0 傷。命中判定純幾何、
// 可單測。學習與遊戲分離:純遊戲機制。

/**
 * 衝擊波預警時間「可躲」門檻 (ms)。任何指派 shockwave 的 boss,warnMs 必須 ≥
 * 這個值,玩家 (K-3) 才有足夠時間從圓心走到圈外。單測對所有指派處強制此下限。
 *
 * 推導:玩家移速約 260 px/s,大圈半徑 ≤ 320 px → 從圓心走出最遠約 320 px 需
 * ~1.23s;留裕度取 1000ms 為「絕對可躲」下限 (實際指派一律給更長,如 1200ms)。
 */
export const SHOCKWAVE_MIN_SAFE_WARN_MS = 1000;

/**
 * 判定一個點 (玩家) 是否在衝擊波判定圈內 (圈內才受傷,圈外安全)。
 * 用「圓心距 ≤ 半徑 + 玩家半徑」近似 (玩家半徑預設 16,跟雷射命中一致)。
 *
 * @param cx       衝擊波圓心 x (spawn 當下鎖定)
 * @param cy       衝擊波圓心 y
 * @param radius   判定半徑 (px)
 * @param px       玩家 x
 * @param py       玩家 y
 * @param playerHalf 玩家半徑近似 (預設 16)
 */
export function shockwaveHitsPlayer(
  cx: number,
  cy: number,
  radius: number,
  px: number,
  py: number,
  playerHalf = 16,
): boolean {
  const dist = Math.hypot(px - cx, py - cy);
  return dist <= radius + playerHalf;
}

// ─── uniqueGimmick 'teleport' 行為 (boss-teleport-gimmick) ─────────────────────
//
// BossDef.uniqueGimmick:'teleport' 文件定義 =「HP < 50% 後每 5s 瞬移到隨機 x」。
// 先前只有 techno 門派透過 FACTION_MECHANIC 真的會瞬移;此純函式讓「任何帶
// uniqueGimmick:'teleport' 的 boss」都實際生效 (不限門派)。scene 每幀呼叫一次:
// 條件達成就 mutate boss.x 並回傳 true (供 scene 播瞬移特效)。瞬移落點帶邊界
// clamp + 跟玩家拉開最小距離,避免「貼臉瞬移」無法閃 (老闆鐵則:躲得掉)。
// 學習與遊戲分離:純遊戲機制。

/** teleport gimmick 觸發節奏 (ms):HP<50% 後每 5s 瞬移一次。 */
export const TELEPORT_GIMMICK_INTERVAL_MS = 5000;
/** teleport gimmick 觸發的 HP 比例門檻 (< 50% 才開始瞬移)。 */
export const TELEPORT_GIMMICK_HP_RATIO = 0.5;
/** 瞬移落點與玩家 x 的最小水平間隔 (px),避免貼臉瞬移無法閃。 */
export const TELEPORT_GIMMICK_MIN_PLAYER_GAP = 140;

/** 瞬移 gimmick 的可變每隻狀態 (scene 端 per-spawn 持有)。 */
export interface TeleportGimmickState {
  /** 上次瞬移的 wall-clock (ms);-Infinity = 從未瞬移 (首次即可觸發)。 */
  lastTeleportMs: number;
}

/** 新的 teleport gimmick 狀態 (每次 boss spawn 建一個)。 */
export function makeTeleportGimmickState(): TeleportGimmickState {
  return { lastTeleportMs: -Infinity };
}

/**
 * 對帶 uniqueGimmick:'teleport' 的 boss 跑「一次」瞬移判定。
 * 條件:gimmick=='teleport' 且 HP < 50% 且距上次瞬移 ≥ 5s。達成時 mutate boss.x
 * 到 [minX, maxX] 內的隨機點,且跟玩家 x 至少拉開 MIN_PLAYER_GAP (往玩家反方向退),
 * 更新 state.lastTeleportMs,回傳 true (有瞬移);否則回傳 false。
 *
 * @param boss     live Boss (mutate boss.x)
 * @param nowMs    現在 wall-clock
 * @param state    per-spawn 狀態
 * @param playerX  玩家 x (拉開最小距離用)
 * @param minX     落點下界 (預設 60)
 * @param maxX     落點上界 (預設 GAME.width-60;傳入避免 import 循環)
 * @param rand     隨機源 (預設 Math.random;可注入做確定性測試)
 */
export function stepBossTeleportGimmick(
  boss: Boss,
  nowMs: number,
  state: TeleportGimmickState,
  playerX: number,
  minX: number,
  maxX: number,
  rand: () => number = Math.random,
): boolean {
  if (boss.def.uniqueGimmick !== 'teleport') return false;
  if (!boss.alive) return false;
  if (boss.hp >= boss.hpMax * TELEPORT_GIMMICK_HP_RATIO) return false;
  if (nowMs - state.lastTeleportMs < TELEPORT_GIMMICK_INTERVAL_MS) return false;
  state.lastTeleportMs = nowMs;
  const lo = Math.min(minX, maxX);
  const hi = Math.max(minX, maxX);
  let target = lo + rand() * (hi - lo);
  // 若落點太靠近玩家,往玩家反方向推開到最小間隔 (clamp 回界內)。
  if (Math.abs(target - playerX) < TELEPORT_GIMMICK_MIN_PLAYER_GAP) {
    const away = target >= playerX ? 1 : -1;
    target = playerX + away * TELEPORT_GIMMICK_MIN_PLAYER_GAP;
    target = Math.max(lo, Math.min(hi, target));
  }
  boss.x = target;
  return true;
}

/**
 * 瞬移是否「到點該觸發」(HP<50% 且距上次 ≥ interval),但**不**移動、**不**更新狀態。
 * boss 2026-06-16「瞬移前 1-2 秒在目的地要有明顯記號」:scene 端先用這個判定 due,
 * 拆成「預警(顯示落點記號)→ 1.5s 後才真的移動」兩段。
 */
export function bossTeleportDue(boss: Boss, nowMs: number, state: TeleportGimmickState): boolean {
  if (boss.def.uniqueGimmick !== 'teleport') return false;
  if (!boss.alive) return false;
  if (boss.hp >= boss.hpMax * TELEPORT_GIMMICK_HP_RATIO) return false;
  if (nowMs - state.lastTeleportMs < TELEPORT_GIMMICK_INTERVAL_MS) return false;
  return true;
}

/**
 * 挑一個瞬移落點 x(與 stepBossTeleportGimmick 同邏輯,抽出供「先決定落點 → 預警」用)。
 * 落點在 [minX,maxX] 隨機,且與 playerX 至少拉開 MIN_PLAYER_GAP(往反方向推、clamp 回界內)。
 */
export function pickBossTeleportTarget(
  playerX: number, minX: number, maxX: number, rand: () => number = Math.random,
): number {
  const lo = Math.min(minX, maxX);
  const hi = Math.max(minX, maxX);
  let target = lo + rand() * (hi - lo);
  if (Math.abs(target - playerX) < TELEPORT_GIMMICK_MIN_PLAYER_GAP) {
    const away = target >= playerX ? 1 : -1;
    target = playerX + away * TELEPORT_GIMMICK_MIN_PLAYER_GAP;
    target = Math.max(lo, Math.min(hi, target));
  }
  return target;
}
